import { Configuration, LogLevel } from '@azure/msal-browser';

export interface AuthConfigResponse {
  configured: boolean;
  clientId?: string;
  authority?: string;
  redirectUri?: string;
  scopes?: string[];
  message?: string;
}

let _authConfig: AuthConfigResponse | undefined;

async function fetchWithRetry(url: string, maxAttempts = 3, timeoutMs = 5_000): Promise<Response> {
  let lastErr: unknown;
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      const controller = new AbortController();
      const id = setTimeout(() => controller.abort(), timeoutMs);
      const res = await fetch(url, { signal: controller.signal });
      clearTimeout(id);
      return res;
    } catch (err) {
      lastErr = err;
      if (attempt < maxAttempts) await new Promise(r => setTimeout(r, 2_000));
    }
  }
  throw lastErr;
}

export async function fetchAuthConfig(): Promise<AuthConfigResponse> {
  if (_authConfig) return _authConfig;

  try {
    const res = await fetchWithRetry('/api/auth/config');
    const cfg: AuthConfigResponse = await res.json();
    if (cfg.configured) {
      if (cfg.redirectUri && !cfg.redirectUri.startsWith('http')) {
        cfg.redirectUri = window.location.origin + cfg.redirectUri;
      }
      _authConfig = cfg;
    }
    return cfg;
  } catch {
    return { configured: false, message: 'Failed to fetch auth config' };
  }
}

function resolveRedirectUri(redirectUri?: string): string {
  const fallback = new URL('/auth/callback', window.location.origin).toString();

  if (!redirectUri) {
    return fallback;
  }

  try {
    return new URL(redirectUri, window.location.origin).toString();
  } catch {
    return fallback;
  }
}

export function buildMsalConfig(authConfig: AuthConfigResponse): Configuration {
  return {
    auth: {
      clientId: authConfig.clientId || '',
      authority: authConfig.authority || '',
      redirectUri: resolveRedirectUri(authConfig.redirectUri),
      postLogoutRedirectUri: window.location.origin,
      navigateToLoginRequestUrl: false,
    },
    cache: {
      cacheLocation: 'localStorage',
      storeAuthStateInCookie: false,
    },
    system: {
      loggerOptions: {
        logLevel: LogLevel.Info,
        loggerCallback: (level, message) => {
          if (level <= LogLevel.Warning) console.log('[msal]', message);
        },
      },
    },
  };
}

export function getLoginScopes(authConfig: AuthConfigResponse): string[] {
  return authConfig.scopes || ['openid', 'profile', 'email'];
}
